@use "/src/styles/functions";
@use "/src/styles/mixins";

@property --d1 {
  syntax: "<number>";
  inherits: false;
  initial-value: 16;
}

.button {
  display: flex;

  transition: all 0.3s ease-in-out;
  cursor: pointer;

  border-radius: functions.radius(10);
  overflow: hidden;

  --button-base-background-color: rgba(var(--theme-primary-base));
  --button-hover-background-color: rgba(var(--theme-primary-dark));
  --button-base-color: rgba(var(--theme-static-static-white));
  --button-hover-color: rgba(var(--theme-static-static-white));
  --button-border-base-color: rgba(var(--theme-primary-base));
  --button-border-hover-border-color: rgba(var(--theme-primary-base));
  --button-focus-border-color: rgba(var(--theme-primary-base));
  --button-focus-stroke-color: rgba(var(--theme-primary-base), 0.24);

  background-color: var(--button-base-background-color);
  border: 1px solid var(--button-border-base-color);
  color: var(--button-base-color);
  outline-offset: 2px;
  outline: 2px solid transparent;
  &[data-style="fancy"] {
    transition:
      all 0.3s ease-in-out,
      --d1 0.3s ease-in;
    background-image: linear-gradient(
      180deg,
      rgba(255, 255, 255, calc(var(--d1) / 100)) 0%,
      rgba(255, 255, 255, 0) 100%
    );
  }
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  &:hover {
    background-color: var(--button-hover-background-color);
    border: 1px solid var(--button-border-hover-border-color);
    color: var(--button-hover-color);

    &[data-style="fancy"] {
      --d1: 32;
    }
  }

  &:focus,
  &:active {
    outline: 2px solid var(--button-focus-stroke-color);
    border: 1px solid var(--button-focus-border-color);

    &[data-style="fancy"] {
      background-image: linear-gradient(
        180deg,
        rgba(255, 255, 255, 0.16) 0%,
        rgba(255, 255, 255, 0) 100%
      );
      outline: 2px solid transparent;

      box-shadow: 0 0 0 4px var(--button-focus-stroke-color);
    }
  }

  & * {
    --uib-color: var(--button-base-color) !important;
  }

  &__container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 2.5rem;
    padding: functions.space(10);
    gap: functions.space(4);
    width: 100%;
    height: 100%;
  }

  &__icon {
    flex: 0 0 auto;
  }

  &[data-size="medium"] {
    border-radius: functions.radius(10);

    .button__container {
      min-height: 2.5rem;
      padding: functions.space(10);
      gap: functions.space(4);
    }
  }
  &[data-size="small"] {
    border-radius: functions.radius(8);

    .button__container {
      min-height: 2.25rem;
      padding: functions.space(8);
      gap: functions.space(4);
    }
  }
  &[data-size="x-small"] {
    border-radius: functions.radius(8);

    .button__container {
      min-height: 2rem;
      padding: functions.space(6);
      gap: functions.space(2);
    }
  }
  &[data-size="2x-small"] {
    border-radius: functions.radius(8);

    .button__container {
      min-height: 1.75rem;
      padding: functions.space(4) functions.space(6);
      gap: functions.space(2);
    }
  }

  &[data-style="filled"] {
    &[data-variant="primary"] {
      --button-base-background-color: rgba(var(--theme-primary-base));
      --button-hover-background-color: rgba(var(--theme-primary-dark));
      --button-base-color: rgba(var(--theme-static-static-white));
      --button-hover-color: rgba(var(--theme-static-static-white));
      --button-border-base-color: rgba(var(--theme-primary-base));
      --button-border-hover-border-color: rgba(var(--theme-primary-base));
      --button-focus-border-color: rgba(var(--theme-primary-base));
      --button-focus-stroke-color: rgba(var(--theme-primary-base), 0.24);
    }
    &[data-variant="neutral"] {
      --button-base-background-color: rgba(var(--theme-bg-strong-950));
      --button-hover-background-color: rgba(var(--theme-bg-surface-800));
      --button-base-color: rgba(var(--theme-text-white-0));
      --button-hover-color: rgba(var(--theme-text-white-0));
      --button-border-base-color: rgba(var(--theme-bg-strong-950));
      --button-border-hover-border-color: rgba(var(--theme-bg-surface-800));
      --button-focus-border-color: rgba(var(--theme-bg-strong-950));
      --button-focus-stroke-color: rgba(var(--theme-state-faded-light));
    }
    &[data-variant="success"] {
      --button-base-background-color: rgba(var(--theme-state-success-base));
      --button-hover-background-color: var(--color-green-700);
      --button-base-color: rgba(var(--theme-static-static-white));
      --button-hover-color: rgba(var(--theme-static-static-white));
      --button-border-base-color: rgba(var(--theme-state-success-base));
      --button-border-hover-border-color: var(--color-green-700);
      --button-focus-border-color: rgba(var(--theme-state-success-base));
      --button-focus-stroke-color: rgba(var(--theme-state-success-light));
    }
    &[data-variant="error"] {
      --button-base-background-color: rgba(var(--theme-state-error-base));
      --button-hover-background-color: var(--color-red-700);
      --button-base-color: rgba(var(--theme-static-static-white));
      --button-hover-color: rgba(var(--theme-static-static-white));
      --button-border-base-color: rgba(var(--theme-state-error-base));
      --button-border-hover-border-color: var(--color-red-700);
      --button-focus-border-color: rgba(var(--theme-state-error-base));
      --button-focus-stroke-color: rgba(var(--theme-state-error-light));
    }
  }
  &[data-style="stroke"] {
    &[data-variant="primary"] {
      --button-base-background-color: rgba(var(--theme-bg-white-0));
      --button-hover-background-color: rgba(var(--theme-primary-base), 0.1);
      --button-base-color: rgba(var(--theme-primary-base));
      --button-hover-color: rgba(var(--theme-primary-base));
      --button-border-base-color: rgba(var(--theme-primary-base));
      --button-border-hover-border-color: rgba(var(--theme-primary-base), 0.1);
      --button-focus-border-color: rgba(var(--theme-primary-base));
      --button-focus-stroke-color: rgba(var(--theme-primary-base), 0.24);
    }
    &[data-variant="neutral"] {
      --button-base-background-color: rgba(var(--theme-bg-white-0));
      --button-hover-background-color: rgba(var(--theme-bg-weak-50));
      --button-base-color: rgba(var(--theme-text-sub-600));
      --button-hover-color: rgba(var(--theme-text-strong-900));
      --button-border-base-color: rgba(var(--theme-stroke-soft-200));
      --button-border-hover-border-color: rgba(var(--theme-bg-weak-50));
      --button-focus-border-color: rgba(var(--theme-bg-strong-950));
      --button-focus-stroke-color: rgba(var(--theme-state-faded-light));
    }
    &[data-variant="success"] {
      --button-base-background-color: rgba(var(--theme-bg-white-0));
      --button-hover-background-color: var(--color-alpha-colors-green-alpha-10);
      --button-base-color: rgba(var(--theme-state-success-base));
      --button-hover-color: rgba(var(--theme-state-success-base));
      --button-border-base-color: rgba(var(--theme-state-success-base));
      --button-border-hover-border-color: var(
        --color-alpha-colors-green-alpha-10
      );
      --button-focus-border-color: rgba(var(--theme-state-success-base));
      --button-focus-stroke-color: rgba(var(--theme-state-success-light));
    }
    &[data-variant="error"] {
      --button-base-background-color: rgba(var(--theme-bg-white-0));
      --button-hover-background-color: var(--color-alpha-colors-red-alpha-10);
      --button-base-color: rgba(var(--theme-state-error-base));
      --button-hover-color: rgba(var(--theme-state-error-base));
      --button-border-base-color: rgba(var(--theme-state-error-base));
      --button-border-hover-border-color: var(
        --color-alpha-colors-red-alpha-10
      );
      --button-focus-border-color: rgba(var(--theme-state-error-base));
      --button-focus-stroke-color: rgba(var(--theme-state-error-light));
    }
  }
  &[data-style="lighter"] {
    &[data-variant="primary"] {
      --button-base-background-color: rgba(var(--theme-primary-base), 0.1);
      --button-hover-background-color: rgba(var(--theme-bg-white-0));
      --button-base-color: rgba(var(--theme-primary-base));
      --button-hover-color: rgba(var(--theme-primary-base));
      --button-border-base-color: rgba(var(--theme-primary-base), 0.1);
      --button-border-hover-border-color: rgba(var(--theme-primary-base));
      --button-focus-border-color: rgba(var(--theme-primary-base));
      --button-focus-stroke-color: rgba(var(--theme-primary-base), 0.24);
    }
    &[data-variant="neutral"] {
      --button-base-background-color: rgba(var(--theme-bg-weak-50));
      --button-hover-background-color: rgba(var(--theme-bg-white-0));
      --button-base-color: rgba(var(--theme-text-sub-600));
      --button-hover-color: rgba(var(--theme-text-strong-900));
      --button-border-base-color: rgba(var(--theme-bg-weak-50));
      --button-border-hover-border-color: rgba(var(--theme-stroke-soft-200));
      --button-focus-border-color: rgba(var(--theme-bg-strong-950));
      --button-focus-stroke-color: rgba(var(--theme-state-faded-light));
    }
    &[data-variant="success"] {
      --button-base-background-color: var(--color-alpha-colors-green-alpha-10);
      --button-hover-background-color: rgba(var(--theme-bg-white-0));
      --button-base-color: rgba(var(--theme-state-success-base));
      --button-hover-color: rgba(var(--theme-state-success-base));
      --button-border-base-color: var(--color-alpha-colors-green-alpha-10);
      --button-border-hover-border-color: rgba(var(--theme-state-success-base));
      --button-focus-border-color: rgba(var(--theme-state-success-base));
      --button-focus-stroke-color: rgba(var(--theme-state-success-light));
    }
    &[data-variant="error"] {
      --button-base-background-color: var(--color-alpha-colors-red-alpha-10);
      --button-hover-background-color: rgba(var(--theme-bg-white-0));
      --button-base-color: rgba(var(--theme-state-error-base));
      --button-hover-color: rgba(var(--theme-state-error-base));
      --button-border-base-color: var(--color-alpha-colors-red-alpha-10);
      --button-border-hover-border-color: rgba(var(--theme-state-error-base));
      --button-focus-border-color: rgba(var(--theme-state-error-base));
      --button-focus-stroke-color: rgba(var(--theme-state-error-light));
    }
  }
  &[data-style="ghost"] {
    &[data-variant="primary"] {
      --button-base-background-color: rgba(var(--theme-bg-white-0), 0);
      --button-hover-background-color: rgba(var(--theme-primary-base), 0.1);
      --button-base-color: rgba(var(--theme-primary-base));
      --button-hover-color: rgba(var(--theme-primary-base));
      --button-border-base-color: rgba(var(--theme-primary-base), 0);
      --button-border-hover-border-color: rgba(var(--theme-primary-base), 0.1);
      --button-focus-border-color: rgba(var(--theme-primary-base));
      --button-focus-stroke-color: rgba(var(--theme-primary-base), 0.24);
    }
    &[data-variant="neutral"] {
      --button-base-background-color: rgba(var(--theme-bg-white-0), 0);
      --button-hover-background-color: rgba(var(--theme-bg-weak-50));
      --button-base-color: rgba(var(--theme-text-sub-600));
      --button-hover-color: rgba(var(--theme-text-strong-900));
      --button-border-base-color: rgba(var(--theme-stroke-soft-200), 0);
      --button-border-hover-border-color: rgba(var(--theme-bg-weak-50));
      --button-focus-border-color: rgba(var(--theme-bg-strong-950));
      --button-focus-stroke-color: rgba(var(--theme-state-faded-light));
    }
    &[data-variant="success"] {
      --button-base-background-color: rgba(var(--theme-bg-white-0), 0);
      --button-hover-background-color: var(--color-alpha-colors-green-alpha-10);
      --button-base-color: rgba(var(--theme-state-success-base));
      --button-hover-color: rgba(var(--theme-state-success-base));
      --button-border-base-color: rgba(var(--theme-state-success-base), 0);
      --button-border-hover-border-color: var(
        --color-alpha-colors-green-alpha-10
      );
      --button-focus-border-color: rgba(var(--theme-state-success-base));
      --button-focus-stroke-color: rgba(var(--theme-state-success-light));
    }
    &[data-variant="error"] {
      --button-base-background-color: rgba(var(--theme-bg-white-0));
      --button-hover-background-color: var(--color-alpha-colors-red-alpha-10);
      --button-base-color: rgba(var(--theme-state-error-base));
      --button-hover-color: rgba(var(--theme-state-error-base));
      --button-border-base-color: rgba(var(--theme-state-error-base), 0);
      --button-border-hover-border-color: var(
        --color-alpha-colors-red-alpha-10
      );
      --button-focus-border-color: rgba(var(--theme-state-error-base));
      --button-focus-stroke-color: rgba(var(--theme-state-error-light));
    }
  }

  &[data-style="fancy"] {
    &[data-variant="primary"] {
      --button-base-background-color: rgba(var(--theme-primary-base));
      --button-hover-background-color: rgba(var(--theme-primary-base));
      --button-base-color: rgba(var(--theme-static-static-white));
      --button-hover-color: rgba(var(--theme-static-static-white));
      --button-border-base-color: rgba(var(--theme-primary-base));
      --button-border-hover-border-color: rgba(var(--theme-primary-base));
      --button-focus-border-color: rgba(var(--theme-primary-base));
      --button-focus-stroke-color: rgba(var(--theme-primary-base), 0.24);
      box-shadow: 0px 1px 2px rgba(14, 18, 27, 0.48);
    }
    &[data-variant="secondary"] {
      --button-base-background-color: rgba(var(--theme-secondary-600));
      --button-hover-background-color: rgba(var(--theme-secondary-600));
      --button-base-color: rgba(var(--theme-static-static-white));
      --button-hover-color: rgba(var(--theme-static-static-white));
      --button-border-base-color: rgba(var(--theme-secondary-600));
      --button-border-hover-border-color: rgba(var(--theme-secondary-600));
      --button-focus-border-color: rgba(var(--theme-secondary-600));
      --button-focus-stroke-color: rgba(var(--theme-secondary-600), 0.24);
      box-shadow: 0px 1px 2px rgba(14, 18, 27, 0.48);
    }
    &[data-variant="neutral"] {
      --button-base-background-color: rgba(var(--theme-bg-strong-950));
      --button-hover-background-color: rgba(var(--theme-bg-strong-950));
      --button-base-color: rgba(var(--theme-text-white-0));
      --button-hover-color: rgba(var(--theme-text-white-0));
      --button-border-base-color: rgba(var(--theme-bg-strong-950));
      --button-border-hover-border-color: rgba(var(--theme-bg-strong-950));
      --button-focus-border-color: rgba(var(--theme-bg-strong-950));
      --button-focus-stroke-color: rgba(var(--theme-bg-strong-950), 0.24);
      box-shadow: 0px 1px 2px rgba(14, 18, 27, 0.48);
    }
    &[data-variant="success"] {
      --button-base-background-color: rgba(var(--theme-state-success-base));
      --button-hover-background-color: rgba(var(--theme-state-success-base));
      --button-base-color: rgba(var(--theme-static-static-white));
      --button-hover-color: rgba(var(--theme-static-static-white));
      --button-border-base-color: rgba(var(--theme-state-success-base));
      --button-border-hover-border-color: rgba(var(--theme-state-success-base));
      --button-focus-border-color: rgba(var(--theme-state-success-base));
      --button-focus-stroke-color: rgba(var(--theme-state-success-base), 0.24);
      box-shadow: 0px 1px 2px rgba(14, 18, 27, 0.48);
    }
    &[data-variant="error"] {
      --button-base-background-color: rgba(var(--theme-state-error-base));
      --button-hover-background-color: rgba(var(--theme-state-error-base));
      --button-base-color: rgba(var(--theme-static-static-white));
      --button-hover-color: rgba(var(--theme-static-static-white));
      --button-border-base-color: rgba(var(--theme-state-error-base));
      --button-border-hover-border-color: rgba(var(--theme-state-error-base));
      --button-focus-border-color: rgba(var(--theme-state-error-base));
      --button-focus-stroke-color: rgba(var(--theme-state-error-base), 0.24);
      box-shadow: 0px 1px 2px rgba(14, 18, 27, 0.48);
    }
    &[data-variant="basic"] {
      --button-base-background-color: rgba(var(--theme-bg-white-0));
      --button-hover-background-color: rgba(var(--theme-bg-weak-50));
      --button-base-color: rgba(var(--theme-text-sub-600));
      --button-hover-color: rgba(var(--theme-text-strong-900));
      --button-border-base-color: rgba(var(--theme-stroke-soft-200));
      --button-border-hover-border-color: rgba(var(--theme-stroke-soft-200));
      --button-focus-border-color: rgba(var(--theme-stroke-soft-200));
      --button-focus-stroke-color: rgba(var(--theme-stroke-soft-200), 0.24);
      box-shadow: 0px 1px 3px rgba(14, 18, 27, 0.12);
    }
  }

  &[data-disabled="true"] {
    cursor: not-allowed;
    --button-base-background-color: rgba(var(--theme-bg-weak-50)) !important;
    --button-hover-background-color: rgba(var(--theme-bg-weak-50)) !important;
    --button-base-color: rgba(var(--theme-icon-disabled-300)) !important;
    --button-hover-color: rgba(var(--theme-icon-disabled-300)) !important;
    --button-border-base-color: rgba(var(--theme-bg-weak-50)) !important;
    --button-border-hover-border-color: rgba(
      var(--theme-bg-weak-50)
    ) !important;
    --button-focus-border-color: rgba(var(--theme-bg-weak-50), 0) !important;
    --button-focus-stroke-color: rgba(var(--theme-bg-weak-50), 0) !important;
  }

  &__link {
    width: 100%;
    text-decoration: none;
    color: unset;

    &[data-underline="true"] {
      //text-decoration: underline;
    }
    &:hover,
    &:active,
    &:focus,
    &:visited {
      //text-decoration: underline;
      color: unset;
    }
  }

  &__loader {
    padding: 0 functions.space(16);
  }

  &__flex0 {
    flex: 0 0 auto;
  }
}

.defaultCursor {
  cursor: default;
}
